import { Attribute, CustomAttributeRegistry, Input, Modifier } from '@rhtml/custom-attributes';

@Modifier({
  selector: 'scroll-observer',
  registry(this: HTMLElement) {
    return new CustomAttributeRegistry(this);
  },
})
export class ScrollObserver extends Attribute {
  @Input({ observe: true })
  threshold = '0.1';

  @Input({ observe: true })
  rootMargin = '0px 0px -50px 0px';

  private intersectionObserver: IntersectionObserver | null = null;
  private hasAnimated = false;

  OnInit() {
    // console.log('ScrollObserver: OnInit called');
    // Hide the element initially
    this.element.style.opacity = '0';
    this.element.style.transform = 'translateY(20px)';
    this.element.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
    this.setupObserver();
  }

  OnDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  OnUpdate() {
    this.hasAnimated = false;
    this.element.classList.remove('animate-in');
    // Reset to hidden state
    this.element.style.opacity = '0';
    this.element.style.transform = 'translateY(20px)';
  }

  private setupObserver() {
    const threshold = parseFloat(this.threshold) || 0.1;
    const rootMargin = this.rootMargin || '0px 0px -50px 0px';

    // console.log('ScrollObserver: Setting up observer with threshold:', threshold);

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // console.log('ScrollObserver: Element intersecting:', entry.isIntersecting, 'hasAnimated:', this.hasAnimated);
          if (entry.isIntersecting && !this.hasAnimated) {
            // console.log('ScrollObserver: Triggering animation');
            this.triggerAnimation();
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    this.intersectionObserver.observe(this.element);
    // console.log('ScrollObserver: Observer attached to element');
  }

  private triggerAnimation() {
    // console.log('ScrollObserver: Animation triggered');
    this.hasAnimated = true;

    // Show the element first
    this.element.style.opacity = '1';
    this.element.style.transform = 'translateY(0)';

    // Add animate-in class
    this.element.classList.add('animate-in');

    // Trigger any existing animated attributes by dispatching a custom event
    const animatedElements = this.element.querySelectorAll('[animated]');
    // console.log('ScrollObserver: Found animated elements:', animatedElements.length);

    animatedElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        // Force the animation by dispatching a custom event
        const event = new CustomEvent('animation-trigger', {
          bubbles: true,
          detail: { element: el },
        });
        el.dispatchEvent(event);

        // Also try to force a re-render by toggling classes
        const animatedClass = el.getAttribute('animated');
        if (animatedClass) {
          // console.log('ScrollObserver: Re-triggering animation for:', animatedClass);
          el.classList.remove(animatedClass);
          el.classList.remove('animated');
          setTimeout(() => {
            el.classList.add('animated');
            el.classList.add(animatedClass);
          }, 50);
        }
      }
    });
  }
}
